import { FC, useState, useEffect } from 'react';
import { NextSeo } from 'next-seo';
import Image from 'next/image';
import Axios from 'axios';
import cn from 'classnames';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';

//Style
import s from './product-view.module.scss';

// Components
import { HTMLContent, Tabs, Tab } from 'components/common';
import { Breadcrumbs, BreadcrumbItem } from 'components/ui';
import { Quantity } from 'components/inputs';
import { ProductSlider, ProductCard } from 'components/product';
import ProductRelationsCards from '../ProductRelationsCards';

// Hooks
import { useI18n } from 'hooks/useI18n';
import useBasket from 'hooks/useBasket';
import useAuth from 'hooks/useAuth';

// Interfaces
import { ProductDetail, ProductRelations } from 'interfaces/Storm/ProductDetail';
import { ProductListItem } from 'interfaces/Storm/Product';

// Utils
import { measureProductDetailView } from 'utils/tracking';

//services
import { CfCategory } from 'interfaces/Contentful';

interface Props {
  product: ProductDetail;
  category: CfCategory | null;
  productRelations: ProductRelations[];
}

const ProductView: FC<Props> = ({ product, category, productRelations }) => {
  const { currency } = useI18n();
  const { formatMessage } = useIntl();
  const { addProduct } = useBasket();
  const { isLoggedIn } = useAuth();

  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    function updateViewCount(productId: string, categoryId: string) {
      Axios.post(
        '/api/analytics/view-count',
        {},
        {
          params: {
            productId: productId,
            categoryId: categoryId,
          },
        }
      );
    }
    const viewCountTimeout = setTimeout(() => {
      if (product.id && product.category.externalId) {
        updateViewCount(product.id, product.category.externalId);
      }
    }, 3000);

    measureProductDetailView(product);

    return () => {
      if (viewCountTimeout) clearTimeout(viewCountTimeout);
    };
  }, [product]);

  const handleOnQuantityChange = (quantity: number) => {
    setQuantity(quantity);
  };

  const handleOnAddToCart = () => {
    addProduct(product.partNo, quantity);
  };

  const productImage = product.primaryImageUrl;

  const additionalFiles = product.files?.map((file) => ({
    id: file.externalId,
    src: file.imageUrl,
    name: file.name,
  }));

  const getParametricVal = (val: string) => {
    switch (val) {
      case 'True': {
        return (
          <span>
            {formatMessage({
              id: 'product-view.specifications.truthyValue',
              description: 'The label for a specification were a value is true',
              defaultMessage: 'Ja',
            })}
          </span>
        );
      }
      case 'False': {
        return (
          <span>
            {formatMessage({
              id: 'product-view.specifications.falseValue',
              description: 'The label for a specification were a value is false',
              defaultMessage: 'Nej',
            })}
          </span>
        );
      }
      case null:
      case '': {
        return (
          <span>
            {formatMessage({
              id: 'product-view.specifications.nullValue',
              description: 'The label for a specification were a value is null or empty',
              defaultMessage: 'Ingen info kan visas',
            })}
          </span>
        );
      }
      default: {
        return val;
      }
    }
  };

  const getParametricIcon = (val: string) => {
    if (val.includes('PVC')) {
      return (
        <svg xmlns='http://www.w3.org/2000/svg' width='24' height='32'>
          <g fill='none'>
            <g fill='#000'>
              <path d='M19.51 22.23h-4.665a.557.557 0 01-.557-.555c0-.306.25-.554.557-.554h4.666c1.108 0 2.278-.73 2.907-1.816.605-1.04.619-2.466.036-3.466l-3.442-5.932a.552.552 0 01.205-.758.555.555 0 01.763.204l3.441 5.933c.784 1.35.77 3.189-.034 4.574-.838 1.438-2.358 2.37-3.876 2.37zM5.001 9.255a.556.556 0 01-.482-.831l3.616-6.225a4.445 4.445 0 017.674.001l2.703 4.65a.549.549 0 01-.208.757.553.553 0 01-.76-.203l-2.699-4.65A3.33 3.33 0 009.1 2.753L5.484 8.978a.56.56 0 01-.483.277zm6.5 12.975H4.436c-1.474 0-2.982-.953-3.837-2.427a4.552 4.552 0 010-4.515l2.454-4.464a.561.561 0 01.758-.22c.27.147.368.486.22.754L1.57 15.833c-.599 1.034-.599 2.401-.007 3.422.663 1.135 1.789 1.87 2.873 1.87H11.5a.553.553 0 110 1.105z' />
              <path d='M10.438 25a.552.552 0 01-.319-.1.551.551 0 01-.138-.771l1.723-2.452-1.72-2.417a.554.554 0 01.132-.772.56.56 0 01.777.132l1.947 2.736a.555.555 0 01.002.637l-1.946 2.77a.559.559 0 01-.458.237zM4.584 9.977a.563.563 0 01-.526-.372l-.983-2.8a.552.552 0 01.343-.705c.29-.107.61.05.711.34l.832 2.375 2.587-.34a.561.561 0 01.628.477.56.56 0 01-.482.624l-3.037.398c-.025.001-.048.003-.073.003zM17.738 11.7a.553.553 0 01-.5-.804l1.231-2.411a.548.548 0 01.528-.3l3.034.162a.56.56 0 01.527.583c-.014.305-.276.575-.585.523l-2.67-.145-1.064 2.086a.564.564 0 01-.501.307z' />
            </g>
            <path
              fill='#181D1F'
              d='M6.781 31.88v-1.773h.854c.345 0 .632-.041.86-.124.227-.082.408-.193.541-.333.134-.14.227-.303.28-.49.052-.187.078-.382.078-.587a1.9 1.9 0 00-.079-.55 1.09 1.09 0 00-.276-.463 1.376 1.376 0 00-.541-.32c-.23-.08-.522-.12-.877-.12H6v4.76h.781zm.834-2.447H6.78v-1.646h.827c.197 0 .36.017.49.053a.801.801 0 01.308.153.53.53 0 01.16.25c.031.1.047.217.047.35 0 .13-.015.245-.043.347a.58.58 0 01-.154.263.733.733 0 01-.305.17c-.13.04-.295.06-.496.06zm4.464 2.447l1.779-4.76h-.847l-1.306 3.667-1.287-3.667h-.886l1.785 4.76h.762zm3.965.12c.306 0 .575-.043.807-.13.232-.087.43-.208.591-.363.162-.156.289-.338.38-.547.093-.209.152-.438.178-.687h-.84c-.022.16-.058.304-.109.43a.961.961 0 01-.206.324.895.895 0 01-.325.206 1.31 1.31 0 01-.47.074c-.205 0-.388-.041-.548-.124a1.15 1.15 0 01-.41-.36 1.72 1.72 0 01-.26-.57 2.982 2.982 0 01-.088-.753c0-.276.028-.524.085-.747.057-.222.141-.412.253-.57.112-.157.248-.279.41-.363.162-.084.348-.127.558-.127.171 0 .318.024.44.07a.868.868 0 01.312.2c.085.087.154.193.207.317.052.124.096.267.131.427l.781-.114a2.742 2.742 0 00-.2-.653c-.09-.196-.21-.363-.361-.503s-.334-.248-.548-.324A2.312 2.312 0 0016.05 27c-.315 0-.604.057-.866.17a1.867 1.867 0 00-.676.493 2.27 2.27 0 00-.437.79 3.347 3.347 0 00-.154 1.054c0 .382.047.727.141 1.036.094.31.232.571.414.787.181.216.404.381.67.497.264.115.565.173.902.173zm-5.371-17c.266 0 .505-.04.716-.118.211-.079.39-.2.536-.363.146-.163.257-.37.333-.62.076-.252.115-.551.115-.899 0-.685-.148-1.19-.442-1.514-.294-.324-.704-.486-1.232-.486-.544 0-.964.166-1.258.497C9.147 11.83 9 12.33 9 13c0 .348.038.648.114.901.077.253.187.46.33.624.144.163.32.283.527.36a2 2 0 00.702.115zm.007-.55c-.327 0-.56-.124-.696-.373-.138-.25-.206-.608-.206-1.077 0-.22.015-.418.046-.596.03-.178.08-.33.15-.456a.758.758 0 01.278-.294.81.81 0 01.428-.105c.17 0 .312.034.428.102a.769.769 0 01.28.291c.073.127.125.28.158.46.032.179.049.378.049.598 0 .223-.017.425-.05.604-.032.18-.084.332-.156.456a.78.78 0 01-.281.289.832.832 0 01-.428.102zm3.738.55c.528 0 .923-.107 1.187-.321.263-.215.395-.509.395-.882a.904.904 0 00-.17-.536c-.113-.16-.3-.285-.562-.376.418-.184.627-.477.627-.88 0-.135-.029-.263-.088-.384s-.149-.227-.271-.319a1.461 1.461 0 00-.464-.22 2.357 2.357 0 00-.66-.082c-.266 0-.492.027-.677.082-.185.055-.34.131-.467.228-.126.098-.226.21-.297.336-.072.126-.126.26-.16.403l.705.083c.066-.209.165-.36.298-.453.133-.094.33-.14.591-.14.2 0 .364.033.49.101a.434.434 0 01.23.322.56.56 0 01-.027.274c-.052.279-.266.418-.64.418h-.523v.555h.49c.266 0 .467.047.604.143.138.095.206.25.206.467 0 .223-.074.386-.222.489-.148.102-.35.154-.608.154-.292 0-.504-.053-.637-.157-.133-.104-.215-.281-.245-.53h-.752a1.5 1.5 0 00.095.434c.054.146.144.278.268.395.124.118.29.213.5.286.21.073.47.11.784.11z'
            />
          </g>
        </svg>
      );
    } else if (val.includes('Halogenfri')) {
      return (
        <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24'>
          <g fill='none' fillRule='evenodd'>
            <path
              d='M6.108 17.892a.363.363 0 00.519 0l1.758-1.758a4.842 4.842 0 003.179 1.183c1.304 0 2.53-.508 3.45-1.43C16.862 14.04 16.996 7.645 17 7.374A.367.367 0 0016.626 7c-.271.005-6.667.14-8.514 1.984a4.887 4.887 0 00-.246 6.63l-1.759 1.76a.366.366 0 000 .518zm2.524-8.389c1.27-1.27 5.618-1.684 7.623-1.758-.074 2.005-.488 6.351-1.76 7.623a4.119 4.119 0 01-2.931 1.215 4.117 4.117 0 01-2.66-.968l3.856-3.858a.366.366 0 10-.518-.519l-3.857 3.858a4.154 4.154 0 01.247-5.593z'
              fill='#000'
              fillRule='nonzero'
            />
            <circle cx='12' cy='12' r='11.5' stroke='#0B090A' />
          </g>
        </svg>
      );
    }
  };

  const highlightedParametrics = product.highlightedParametrics?.map((parametric) => ({
    name: parametric.name,
    icon: getParametricIcon(parametric.name),
  }));

  const createBreadcrumbs = ({title, slug}:CfCategory) => {

    const titleArray = title.split('/');
    const slugArray = slug.split('/');
   
    const breadcrumbLinks = titleArray.map((title, index) => {
   
      const slug = slugArray.reduce((acc, current, reduceIndex) => {
        if(reduceIndex > index)
          return acc;
        return `${acc}/${current}`;
      }, '/category');
      
      return {title: title, href: slug, as: slug};
    })
     
   return breadcrumbLinks;
  }
  const breadcrumbs = [{
    title: 'Start',
    href: '/',
    as: '/',
  }];

    category && breadcrumbs.push(...createBreadcrumbs(category));
  
  breadcrumbs.push({
    title: product.name,
    href: `/product/${product.uniqueName}`,
    as: `/product/${product.uniqueName}`,
  });

  const url = new URL(product.primaryImageUrl);

  const hasMainProductImage = url.pathname !== '/';

  const mainProductImage = {
    src: hasMainProductImage ? product.primaryImageUrl : '/defaultW.png',
    alt: hasMainProductImage ? product.name : 'defaultImage',
    id: hasMainProductImage ? product.id : 'defaultImageId'
  }

  return (
    <div>
      <NextSeo
        title={product.name}
        description={product.description}
        openGraph={{
          type: 'website',
          title: product.name,
          description: product.description,
          images: [
            {
              url: `${productImage}?w=800&h=800`,
              width: 800,
              height: 600,
              alt: product.name,
            },
          ],
        }}
      />
      <div className='container max-width-lg margin-y-sm'>
        {breadcrumbs && (
          <Breadcrumbs>
            {breadcrumbs.map((b) => (
              <BreadcrumbItem key={b.href} href={b.href}>
                {b.title}
              </BreadcrumbItem>
            ))}
          </Breadcrumbs>
        )}
      </div>
      <article className='margin-y-lg'>
        <div className='container max-width-adaptive-lg'>
          <div className='grid gap-md justify-between margin-bottom-md'>
            {product.images.length > 0 ? (
              <div className='col-12 col-6@md'>
                <ProductSlider product={product} />
              </div>
            )
            :
              <div className={`items-center flex col-12 col-6@md ${s.productImageWrapper}`} key={mainProductImage.id}>
                <Image
                  key={mainProductImage.id}
                  src={mainProductImage.src}
                  alt={mainProductImage.alt}
                  width={800}
                  height={800}
                  quality={90}
                  objectFit='contain'
                />
              </div>
            }
            <div className='col-12 col-6@md'>
              <div className='margin-bottom-md margin-bottom-lg@md'>
                <div className='flex items-center justify-between width-100%'>
                  <div className='text-component margin-right-sm'>
                    <span className='color-secondary block margin-bottom-xxs'>{product.manufacturer?.name}</span>
                    <h1 className='text-md text-lg@lg'>{product.name}</h1>
                  </div>
                  {isLoggedIn && (
                    <div className='text-sm text-base@md text-right@md' style={{ minWidth: '124px' }}>
                      <FormattedNumber value={product.price} style='currency' currency={currency} />
                      <p className='margin-top-xxs color-contrast-medium text-sm'>exkl. moms | {product.vatRate}%</p>
                    </div>
                  )}
                </div>

                {product.shortDescription && (
                  <div className='text-component margin-top-md color-contrast-high'>
                    <p>{product.shortDescription}</p>
                  </div>
                )}

                {highlightedParametrics && highlightedParametrics?.length > 0 && (
                  <div className='flex flex-row flex-wrap items-center'>
                    {highlightedParametrics?.map((parametric) => (
                      <p key={parametric.name} className='margin-x-xs flex items-center'>
                        <span className='margin-right-xs'>{parametric.icon}</span>
                        {parametric.name}
                      </p>
                    ))}
                  </div>
                )}
              </div>

              <div className={s.metaData}>
                {additionalFiles && additionalFiles.length > 0 && (
                  <div className='margin-right-sm'>
                    {additionalFiles.map((file) => (
                      <a
                        className='margin-y-xxxs color-accent flex items-center flex flex-row'
                        key={file?.id}
                        href={file.src}
                        target='_blank'
                        rel='noreferrer'
                      >
                        <span className='margin-right-xs'>
                          <svg xmlns='http://www.w3.org/2000/svg' width='14' height='18'>
                            <g stroke='#23374A' fill='none' fillRule='evenodd'>
                              <path d='M13.5.5v17H.5V5.682L6.211.5H13.5z' />
                              <path d='M6 1v5H0' />
                              <path strokeLinecap='square' d='M3.563 9.5h7.875m-7.875 2h7.875m-7.838 2h4.8' />
                            </g>
                          </svg>
                        </span>
                        {file.name}
                      </a>
                    ))}
                  </div>
                )}
                <p className=''>
                  <span>
                    {formatMessage({
                      id: 'product-view.partNo',
                      defaultMessage: 'Article number',
                    })}
                  </span>
                  : <span>{product.partNo}</span>
                </p>
              </div>

              {isLoggedIn ? (
                <div className='flex gap-md items-center margin-top-md margin-top-lg@md'>
                  <Quantity name='quantity' value={quantity} onChange={handleOnQuantityChange} />
                  <button className='btn btn--primary btn--md flex-grow' onClick={handleOnAddToCart}>
                    <FormattedMessage id='product-view.addToCartButton' defaultMessage='Add to cart' />
                  </button>
                </div>
              ) : (
                <div className='text-component text-sm font-italic text-center margin-top-md margin-top-lg@md'>
                  <p>
                    <FormattedMessage
                      id='product-view.require-login'
                      defaultMessage='You must login to see the price'
                    />
                  </p>
                </div>
              )}

              <div className='margin-top-md text-sm flex flex-wrap justify-between items-center'>
                {product.availableToSell >= 5 && (
                  <p className='flex items-center'>
                    <span className='margin-right-xs'>
                      <svg xmlns='http://www.w3.org/2000/svg' width='16' height='19'>
                        <path
                          d='M8.206 1.182l6.588 3.636m-7-3.618L1.206 4.8m6.588 4.018L1.206 5.182m7 3.618l6.588-3.6M15 13.775v-8.55m-.206 8.975l-6.588 3.6m-7-3.618l6.588 3.636M1 13.775v-8.55m7 12.55v-8.55'
                          stroke='#666E75'
                          fill='none'
                          fillRule='evenodd'
                          strokeLinecap='round'
                        />
                      </svg>
                    </span>
                    {formatMessage({
                      id: 'product-view.inStock',
                      defaultMessage: 'Stock: In stock',
                    })}
                  </p>
                )}
                {product.availableToSell < 5 && product.availableToSell > 0 && (
                  <p className='flex items-center'>
                    <span className='margin-right-xs'>
                      <svg xmlns='http://www.w3.org/2000/svg' width='16' height='19'>
                        <path
                          d='M8.206 1.182l6.588 3.636m-7-3.618L1.206 4.8m6.588 4.018L1.206 5.182m7 3.618l6.588-3.6M15 13.775v-8.55m-.206 8.975l-6.588 3.6m-7-3.618l6.588 3.636M1 13.775v-8.55m7 12.55v-8.55'
                          stroke='#666E75'
                          fill='none'
                          fillRule='evenodd'
                          strokeLinecap='round'
                        />
                      </svg>
                    </span>
                    {formatMessage({
                      id: 'product-view.lowOnStock',
                      defaultMessage: 'Stock: Few left',
                    })}
                  </p>
                )}
                {product.availableToSell < 1 && (
                  <p className='flex items-center'>
                    <span className='margin-right-xs'>
                      <svg xmlns='http://www.w3.org/2000/svg' width='16' height='19'>
                        <path
                          d='M8.206 1.182l6.588 3.636m-7-3.618L1.206 4.8m6.588 4.018L1.206 5.182m7 3.618l6.588-3.6M15 13.775v-8.55m-.206 8.975l-6.588 3.6m-7-3.618l6.588 3.636M1 13.775v-8.55m7 12.55v-8.55'
                          stroke='#666E75'
                          fill='none'
                          fillRule='evenodd'
                          strokeLinecap='round'
                        />
                      </svg>
                    </span>
                    {formatMessage({
                      id: 'product-view.outOfStock',
                      defaultMessage: 'Stock: On demand',
                    })}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
        <div>
          <Tabs
            className='container max-width-adaptive-lg'
            tabContentClassName='container bg-white max-width-adaptive-lg padding-md padding-lg@md grid gap-sm width-100% margin-0'
          >
            <Tab
              id='tab-description'
              label={formatMessage({
                id: 'product-view.productDescription',
                defaultMessage: 'Description',
                description: 'Title for product description',
              })}
            >
              <HTMLContent
                className={cn('text-component padding-x-xxs margin-y-xs')}
                html={product.description}
              />
            </Tab>

            <Tab
              id='tab-accessories'
              label={formatMessage({
                id: 'product-view.productAccessories',
                defaultMessage: 'Accessories',
                description: 'Title for product accessories',
              })}
            >
              {productRelations && productRelations.length > 0 &&
                <>
                  <div className={`col-12 font-medium margin-bottom-lg ${s.accessorieText}`}>
                    <FormattedMessage
                      id='product-view.productAccessoriesLabel'
                      description={'Accessories'}
                      defaultMessage='Accessories suitable for {message}'
                      values={{ message: product.name }}
                    />
                  </div>
                  {productRelations.map((productR) => {
                    return (
                      <ProductRelationsCards productR={productR} key={productR.id} />
                    )
                  })}
                </>
              }
            </Tab>

            <Tab
              id='tab-specifications'
              label={formatMessage({
                id: 'product-view.productSpecifications',
                defaultMessage: 'Specifications',
                description: 'Title for product specifications',
              })}
            >
              {product.parametricGroups && product.parametricGroups?.length > 0 && (
                <div className={`${product.parametricGroups?.length > 1 ? s.specifications : ''}`}>
                  {product.parametricGroups?.map((group, index) => (
                    <div key={index}>
                      <p className='padding-y-sm font-medium'>{group.groupName}</p>
                      <table className='block'>
                        {group.parametrics?.map((parametric) => (
                          <tr key={parametric.id} className='border-bottom flex flex-row gap-x-sm justify-between'>
                            <td className='padding-y-sm'>{parametric.name}</td>
                            <td className='padding-y-sm'>{getParametricVal(parametric.value)}</td>
                          </tr>
                        ))}
                      </table>
                    </div>
                  ))}
                </div>
              )}
            </Tab>
          </Tabs>
        </div>

        {product.structuredItems && product.structuredItems.itemCount > 0 && (
          <div className='container max-width-adaptive-lg margin-top-lg'>
            <h3 className='margin-bottom-sm'>
              <FormattedMessage id='product-view.structured-items.title' defaultMessage='Included in the package' />
            </h3>
            <div className='flex flex-wrap gap-sm gap-md@md gap-lg@lg'>
              {product.structuredItems.items.map((p) => (
                <div key={p.id} className='col-6 col-4@md col-3@lg bg-sand-lighter'>
                  <ProductCard product={p as unknown as ProductListItem} />
                </div>
              ))}
            </div>
          </div>
        )}
      </article>
    </div>
  );
};

export default ProductView;
